/**
 * Helper utilities for form submission processing
 * Extracted from useFormSubmission hook to improve maintainability and testability
 */

import { RefObject } from 'react';
import { FormikErrors } from 'formik';
import { DynamicFormikProps, FormSubmissionPayload } from '../../types/FormTypes';
import { logDebug, LogTag } from '../../utils/utils';

/**
 * Creates a standardized error payload from unknown error types
 * Handles Error instances, strings, and other unknown error types
 */
export function createErrorPayload(error: unknown): FormSubmissionPayload['error'] {
    if (error instanceof Error) {
        return { message: error.message, rawError: error };
    }
    if (typeof error === 'string') {
        return { message: error, rawError: error };
    }
    return { message: 'An unknown submission error occurred', rawError: error };
}

/**
 * Handles validation errors by setting them in Formik and touching all fields with errors
 * This ensures error messages are visible to the user even if they haven't interacted with the fields
 */
export function handleValidationErrors(errors: FormikErrors<any>, formikRef: RefObject<DynamicFormikProps>): void {
    if (!formikRef.current) return;

    formikRef.current.setErrors(errors);
    touchFieldsWithErrors(errors, formikRef);
}

/**
 * Recursively touches all fields that have validation errors
 * Supports flat, nested object and list errors (e.g. errors.group.items[1].field = 'message')
 */
export function touchFieldsWithErrors(errors: FormikErrors<any>, formikRef: RefObject<DynamicFormikProps>, parentKey = ''): void {
    if (!formikRef.current) return;

    logDebug('FORM_HELPERS', 'Setting touched state for all fields with errors...', undefined, LogTag.FORM_VALIDATION);

    touchErrorLeaves(errors, parentKey, formikRef);
}

// Only leaves are touched: touching a list or group path stores `true` there and hides every <ErrorMessage> below it.
function touchErrorLeaves(errorValue: unknown, path: string, formikRef: RefObject<DynamicFormikProps>): void {
    const formik = formikRef.current;
    if (!formik) return; // Safety check

    if (typeof errorValue === 'string') {
        if (!path) return;
        logDebug('FORM_HELPERS', `Touching field: ${path}`, undefined, LogTag.FORM_VALIDATION);
        // Set field as touched without re-triggering validation (third arg = false)
        formik.setFieldTouched(path, true, false);
        return;
    }

    if (Array.isArray(errorValue)) {
        // forEach skips the holes Formik leaves for list items without errors
        errorValue.forEach((itemError: unknown, index: number) => touchErrorLeaves(itemError, `${path}[${index}]`, formikRef));
        return;
    }

    if (errorValue && typeof errorValue === 'object') {
        Object.entries(errorValue).forEach(([key, nestedError]) => touchErrorLeaves(nestedError, path ? `${path}.${key}` : key, formikRef));
    }
}

/**
 * Safely validates a form with proper cancellation checks
 * Returns validation errors or empty object if validation passes
 */
export async function validateFormSafely(formikRef: RefObject<DynamicFormikProps>, isCancelled: () => boolean): Promise<FormikErrors<any> | null> {
    if (isCancelled() || !formikRef.current) return null;

    try {
        logDebug('FORM_HELPERS', 'Attempting formikRef.current.validateForm()', undefined, LogTag.FORM_VALIDATION);
        const errors = await formikRef.current.validateForm();

        if (isCancelled()) {
            return null; // Check again after async operation
        }

        return errors;
    } catch (error) {
        if (isCancelled()) return null;
        throw error; // Re-throw to be handled by caller
    }
}

/**
 * Safely submits a form with proper error handling and cancellation checks
 * Throws error if submission fails to be handled by caller
 */
export async function submitFormSafely(formikRef: RefObject<DynamicFormikProps>, isCancelled: () => boolean): Promise<void> {
    if (isCancelled()) return;

    if (!formikRef.current) {
        throw new Error('Form reference lost during submission');
    }

    if (typeof formikRef.current.submitForm !== 'function') {
        throw new Error('Form submitForm method is not available');
    }

    try {
        // submitForm triggers the onSubmit defined in the Formik component
        await formikRef.current.submitForm();
    } catch (submitError) {
        throw submitError; // Re-throw to be handled by caller
    }
}
